// Display helpers of the ride list. Rides store the departure as the local
// wall time of the origin (Europe/Sofia), so the list shows that date and
// time as they are and never converts them through the browser time zone.
import type { AdminBookingStatus, AdminRideDetail, AdminRideDevice, RideStatus } from '../../types';

export const STATUS_LABELS: Record<RideStatus, string> = {
  active: 'Активни',
  completed: 'Завършени',
  cancelled: 'Отменени',
  expired: 'Изтекли',
};

export const STATUS_CHIP: Record<RideStatus, { label: string; tone: string }> = {
  active: { label: 'Активно', tone: 'info' },
  completed: { label: 'Завършено', tone: 'success' },
  cancelled: { label: 'Отменено', tone: 'error' },
  expired: { label: 'Изтекло', tone: 'muted' },
};

/** Today in Europe/Sofia as YYYY-MM-DD. */
export const sofiaToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Sofia' }).format(now);

const dayNumber = (isoDate: string): number => Date.UTC(
  Number(isoDate.slice(0, 4)),
  Number(isoDate.slice(5, 7)) - 1,
  Number(isoDate.slice(8, 10)),
) / 86_400_000;

/** "Днес", "Утре", "Вчера", or a short date such as "пт, 7 ноем.". */
export const dayLabel = (isoDate: string, today: string = sofiaToday()): string => {
  const diff = dayNumber(isoDate) - dayNumber(today);
  if (diff === 0) return 'Днес';
  if (diff === 1) return 'Утре';
  if (diff === -1) return 'Вчера';
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('bg-BG', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

/** The full date, for the tooltip and the detail row: "07.11.2026". */
export const fullDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('bg-BG', { timeZone: 'UTC' });

/** "08:30" from "08:30:00". */
export const shortTime = (time: string): string => time.slice(0, 5);

/** A UTC timestamp in Sofia time: "03.11.2026, 08:54". */
export const sofiaDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('bg-BG', {
    timeZone: 'Europe/Sofia',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const seatsLeftText = (left: number): string => {
  if (left === 0) return 'Пълно';
  return left === 1 ? '1 свободно' : `${left} свободни`;
};

export const BOOKING_CHIP: Record<AdminBookingStatus, { label: string; tone: string }> = {
  pending: { label: 'Чака', tone: 'warning' },
  confirmed: { label: 'Потвърдена', tone: 'success' },
  declined: { label: 'Отказана', tone: 'error' },
  completed: { label: 'Завършена', tone: 'success' },
  cancelled: { label: 'Отменена', tone: 'error' },
  expired: { label: 'Изтекла', tone: 'muted' },
};

const SMOKING: Record<AdminRideDetail['smoking'], string> = {
  no: 'Без пушене',
  yes: 'Пушене',
  outside_stops: 'Пушене на спирките',
};

const PETS: Record<AdminRideDetail['pets'], string> = {
  no: 'Без животни',
  yes: 'С животни',
  small_only: 'Малки животни',
};

const MUSIC: Record<string, string> = {
  quiet: 'Тиха музика',
  none: 'Без музика',
  discussion: 'Разговори',
};

/** The driver's choices as one line: "Без пушене · Тиха музика · Без животни · Багаж". */
export const preferencesText = (detail: AdminRideDetail): string =>
  [
    SMOKING[detail.smoking],
    detail.music ? MUSIC[detail.music] ?? detail.music : null,
    PETS[detail.pets],
    detail.luggage ? 'Багаж' : 'Без багаж',
  ]
    .filter(Boolean)
    .join(' · ');

/** "412 км · 4 ч 10 мин", or "-" when the route was never computed. */
export const routeText = (km: number | null, minutes: number | null): string => {
  const parts: string[] = [];
  if (km !== null) parts.push(`${Math.round(km)} км`);
  if (minutes) {
    const hours = Math.floor(minutes / 60);
    parts.push(hours ? `${hours} ч ${minutes % 60} мин` : `${minutes} мин`);
  }
  return parts.length ? parts.join(' · ') : '-';
};

const PLATFORM_LABELS: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Уеб' };

/** One device on one line: "Android 15 · Pixel 8 · v1.7.0". A missing value is left out. */
export const deviceText = (device: AdminRideDevice): string => {
  const platform = PLATFORM_LABELS[device.platform] ?? device.platform;
  return [
    device.os_version ? `${platform} ${device.os_version}` : platform,
    device.device_model,
    device.app_version ? `v${device.app_version}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};
